/* Boonsoir 2.0 — global motion system "De avond valt". See MOTION.md.
   One shared IntersectionObserver, rAF for anything pointer/scroll driven,
   transform / opacity / clip-path / mask only. Everything is skipped with
   prefers-reduced-motion and in the Theme Editor. */
(() => {
  const root = document.documentElement;
  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  const EASE = 'cubic-bezier(.2, 0, 0, 1)';
  const designMode = () => !!(window.Shopify && window.Shopify.designMode) || root.classList.contains('shopify-design-mode') || (document.body && document.body.classList.contains('shopify-design-mode'));
  const still = () => RM.matches || designMode();
  const T = { micro: 180, ui: 360, move: 640, enter: 900, cine: 1300, stagger: 70, line: 90, roll: 420 };
  try { T.roll = parseFloat(getComputedStyle(root).getPropertyValue('--m-roll')) || T.roll; } catch (e) { /* keep default */ }
  const raf = (fn) => window.requestAnimationFrame(fn);
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const fontsReady = () => (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
  const inView = (el, margin = 0) => { const r = el.getBoundingClientRect(); return r.bottom > -margin && r.top < window.innerHeight + margin && r.width > 0; };

  /* =======================================================================
     Shared IntersectionObserver
     ======================================================================= */
  const watchers = new Map();
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting) return;
      const fn = watchers.get(en.target);
      io.unobserve(en.target);
      watchers.delete(en.target);
      if (fn) fn(en.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0 }) : null;
  function watch(el, fn) {
    if (!io) { fn(el); return; }
    const prev = watchers.get(el);
    watchers.set(el, prev ? (t) => { prev(t); fn(t); } : fn);
    io.observe(el);
  }

  /* =======================================================================
     Reveal v2 — [data-reveal], masked heading lines, ink print
     ======================================================================= */
  const SPLIT_SEL = '.display, .h1, .h2, h1, h2';
  const SPLIT_OK = new Set(['EM', 'I', 'STRONG', 'B', 'BR', 'SPAN', 'A', 'SMALL', 'MARK', 'SUP', 'SUB']);
  let booted = false;          // first-screen choreography done
  let fontsDone = false;
  fontsReady().then(() => { fontsDone = true; });

  function revealDuration(el) { return (el.classList.contains('is-split') ? T.enter * .6 : T.enter); }
  function finish(el) {
    if (el._doneT) return;
    const delay = parseFloat(el.style.getPropertyValue('--reveal-d')) || (parseFloat(getComputedStyle(el).getPropertyValue('--reveal-i')) || 0) * T.stagger;
    let lines = 0;
    el.querySelectorAll('[data-split-done]').forEach((h) => { lines = Math.max(lines, h._lines || 0); });
    if (el._lines) lines = Math.max(lines, el._lines);
    const total = delay + Math.max(revealDuration(el), lines ? 60 + lines * T.line + T.enter : 0) + 120;
    el._doneT = setTimeout(() => el.classList.add('is-done'), total);
  }
  function show(el, delay) {
    if (el.classList.contains('is-in')) return;
    if (delay != null) el.style.setProperty('--reveal-d', Math.round(delay) + 'ms');
    if (el.matches('[data-reveal="ink"]')) runInk(el, delay || 0);
    el.classList.add('is-in');
    finish(el);
  }
  function showQuick(el) {
    if (el.classList.contains('is-in')) return;
    el.classList.add('is-quick');
    void el.offsetWidth;
    el.classList.add('is-in');
    if (el.matches('[data-reveal="ink"]')) el.removeAttribute('data-ink');
    setTimeout(() => el.classList.add('is-done'), T.ui + 60);
  }
  function showAll(scope = document) {
    scope.querySelectorAll('[data-reveal]').forEach((el) => { el.classList.add('is-in', 'is-done'); });
    scope.querySelectorAll('[data-ink]').forEach((el) => el.removeAttribute('data-ink'));
  }

  /* ---- Masked heading lines ---- */
  function splitCandidates(scope) {
    const set = new Set();
    scope.querySelectorAll('[data-reveal]').forEach((r) => {
      if (r.matches(SPLIT_SEL) || r.dataset.reveal === 'lines') set.add(r);
      r.querySelectorAll(SPLIT_SEL).forEach((h) => set.add(h));
    });
    return [...set].filter((h) => !h.closest('[data-split="off"], .site-footer, .drawer, .menu-sheet')
      && ![...h.querySelectorAll('*')].some((n) => !SPLIT_OK.has(n.tagName) && !n.classList.contains('split-line') && !n.classList.contains('split-line__inner') && !n.closest('[data-split-word]'))
      && h.textContent.trim().length > 0);
  }
  // Accessible text of a heading: inline marks ([data-split-word], e.g. the wordmark in the hero) read as their label.
  function labelOf(h) {
    let out = '';
    const walk = (node) => {
      [...node.childNodes].forEach((c) => {
        if (c.nodeType === 3) out += c.textContent;
        else if (c.nodeType === 1 && c.hasAttribute('data-split-word')) {
          const l = c.querySelector('[aria-label]');
          const rest = [...c.childNodes].filter((n) => n.nodeType === 3).map((n) => n.textContent).join(''); // e.g. the full stop after the wordmark
          out += ' ' + (c.getAttribute('aria-label') || (l && l.getAttribute('aria-label')) || c.textContent) + rest + ' ';
        }
        else if (c.nodeType === 1 && c.tagName === 'BR') out += ' ';
        else if (c.nodeType === 1) walk(c);
      });
    };
    walk(h);
    return out.replace(/\s+([.,!?;:])/g, '$1').replace(/\s+/g, ' ').trim();
  }
  function unsplit(h) {
    if (h._orig == null) return;
    h.innerHTML = h._orig;
    h.removeAttribute('data-split-done');
  }
  function split(h) {
    if (h._orig == null) h._orig = h.innerHTML; else unsplit(h);
    const words = [];
    let pend = '';
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((p) => {
            if (!p) return;
            if (/^\s+$/.test(p)) { pend += p; frag.appendChild(document.createTextNode(p)); return; }
            const s = document.createElement('span');
            s.textContent = p;
            s._space = pend; pend = '';
            frag.appendChild(s);
            words.push(s);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && child.tagName === 'BR') pend += ' ';
        else if (child.nodeType === 1 && child.hasAttribute('data-split-word')) { child._space = pend; pend = ''; words.push(child); } // an inline mark counts as one word
        else if (child.nodeType === 1) walk(child);
      });
    };
    walk(h);
    if (!words.length) { unsplit(h); return; }
    const label = labelOf(h);
    const fs = parseFloat(getComputedStyle(h).fontSize) || 16;
    const lines = [];
    let top = null;
    words.forEach((w) => {
      const t = w.getBoundingClientRect().top;
      if (top === null || Math.abs(t - top) > fs * .5) { lines.push([]); top = t; }
      lines[lines.length - 1].push(w);
    });
    const chainOf = (w) => { const c = []; let n = w.parentNode; while (n && n !== h) { c.unshift(n); n = n.parentNode; } return c; };
    const out = document.createDocumentFragment();
    lines.forEach((ws, i) => {
      const line = document.createElement('span');
      line.className = 'split-line';
      line.style.setProperty('--line-i', i);
      const inner = document.createElement('span');
      inner.className = 'split-line__inner';
      line.appendChild(inner);
      let stack = []; // [{src, el}]
      ws.forEach((w, wi) => {
        const chain = chainOf(w);
        let k = 0;
        while (k < stack.length && k < chain.length && stack[k].src === chain[k]) k++;
        stack = stack.slice(0, k);
        const host = () => (stack.length ? stack[stack.length - 1].el : inner);
        if (wi > 0 && w._space) host().appendChild(document.createTextNode(w._space.includes(' ') ? w._space : ' '));
        for (let j = k; j < chain.length; j++) {
          const c = chain[j].cloneNode(false);
          c.removeAttribute('id');
          host().appendChild(c);
          stack.push({ src: chain[j], el: c });
        }
        host().appendChild(w.hasAttribute && w.hasAttribute('data-split-word') ? w.cloneNode(true) : document.createTextNode(w.textContent));
      });
      out.appendChild(line);
    });
    if (!h.hasAttribute('aria-label') || h._ownLabel) { h.setAttribute('aria-label', label); h._ownLabel = true; }
    h.innerHTML = '';
    h.appendChild(out);
    h._lines = lines.length;
    h._w = h.clientWidth;
    h.setAttribute('data-split-done', '');
    if (h.matches('[data-reveal]')) h.classList.add('is-split');
  }
  const splitList = new Set();
  function splitScope(scope) {
    if (still()) return;
    splitCandidates(scope).forEach((h) => {
      const host = h.closest('[data-reveal]');
      if (host && host.classList.contains('is-in')) return; // already visible: leave the text alone
      try { split(h); splitList.add(h); } catch (e) { unsplit(h); }
    });
  }
  let resizeT;
  window.addEventListener('resize', () => {
    clearTimeout(resizeT);
    resizeT = setTimeout(() => {
      splitList.forEach((h) => {
        if (!h.isConnected) { splitList.delete(h); return; }
        if (h.clientWidth !== h._w) split(h);
      });
    }, 200);
  }, { passive: true });

  /* ---- Ink print ---- */
  function inkCandidates(scope) {
    return [...scope.querySelectorAll('img.ill, [data-reveal="ink"]')].filter((el) => !el.closest('[data-ink="off"], .drawer, .menu-sheet') && el.getAttribute('data-ink') !== 'off');
  }
  function runInk(el, delay = 0) {
    if (el.getAttribute('data-ink') === 'run') return;
    const go = () => {
      el.style.setProperty('--ink-d', Math.round(delay) + 'ms');
      el.setAttribute('data-ink', 'run');
      el.addEventListener('animationend', function done(e) {
        if (e.target !== el) return;
        el.removeEventListener('animationend', done);
        el.removeAttribute('data-ink');
        el.style.removeProperty('--ink-d');
      });
    };
    if (el.tagName === 'IMG' && !el.complete) {
      let fired = false;
      const once = () => { if (!fired) { fired = true; go(); } };
      el.addEventListener('load', once, { once: true });
      el.addEventListener('error', () => { fired = true; el.removeAttribute('data-ink'); }, { once: true });
      setTimeout(once, 2500);
    } else go();
  }

  function hScroller(el) {
    let n = el.parentElement;
    for (let i = 0; n && n !== document.body && i < 8; i++, n = n.parentElement) {
      if (n.scrollWidth > n.clientWidth + 4) {
        const ox = getComputedStyle(n).overflowX;
        if (ox === 'auto' || ox === 'scroll') return n;
      }
    }
    return null;
  }

  /* ---- Init ---- */
  function prepare(scope, firstScreen) {
    const reveals = [...scope.querySelectorAll('[data-reveal]:not(.is-in)')];
    const inks = inkCandidates(scope).filter((el) => !el.matches('[data-reveal]'));
    if (still() || !io) { showAll(scope); return; }
    // hidden (display:none) illustrations simply stay as they are
    for (let i = inks.length - 1; i >= 0; i--) if (!inks[i].getClientRects().length) inks.splice(i, 1);
    inks.forEach((el) => { if (!el.hasAttribute('data-ink')) el.setAttribute('data-ink', 'wait'); });
    scope.querySelectorAll('[data-reveal="ink"]:not(.is-in)').forEach((el) => el.setAttribute('data-ink', 'wait'));

    const above = [];
    const below = [];
    reveals.forEach((el) => (inView(el) && firstScreen ? above : below).push(el));
    const aboveInk = inks.filter((el) => firstScreen && inView(el));

    // Horizontal scrollers (rails, swipe rows): reveal the whole row when it scrolls into view;
    // items off to the side would otherwise stay blank until swiped.
    const groups = new Map();
    const group = (el, fn) => {
      const sc = hScroller(el);
      if (!sc) return false;
      if (!groups.has(sc)) groups.set(sc, []);
      groups.get(sc).push(fn);
      return true;
    };
    below.forEach((el, i) => { if (!group(el, (k) => show(el, k * T.stagger))) watch(el, () => show(el)); });
    inks.filter((el) => !aboveInk.includes(el)).forEach((el) => {
      const host = el.closest('[data-reveal]');
      const run = () => {
        const d = host && !host.classList.contains('is-done') ? (parseFloat(host.style.getPropertyValue('--reveal-d')) || 0) + 120 : 0;
        runInk(el, d);
      };
      if (!group(el, (k) => runInk(el, k * T.stagger))) watch(el, run);
    });
    groups.forEach((fns, sc) => watch(sc, () => fns.forEach((fn, k) => fn(k))));
    return { above, aboveInk };
  }

  function choreograph(above, aboveInk) {
    if (root.classList.contains('vt-arrival')) {
      above.forEach(showQuick);
      aboveInk.forEach((el) => el.removeAttribute('data-ink'));
      return;
    }
    // first screen: section by section, then --reveal-i inside each section
    const sections = [];
    above.forEach((el) => { const s = el.closest('.shopify-section') || document.body; if (!sections.includes(s)) sections.push(s); });
    above.forEach((el) => {
      const sIdx = sections.indexOf(el.closest('.shopify-section') || document.body);
      const i = parseFloat(getComputedStyle(el).getPropertyValue('--reveal-i')) || 0;
      show(el, 80 + sIdx * 160 + i * 110);
    });
    aboveInk.forEach((el) => {
      const host = el.closest('[data-reveal]');
      runInk(el, host ? (parseFloat(host.style.getPropertyValue('--reveal-d')) || 0) + 160 : 200);
    });
  }

  async function boot() {
    if (still()) { showAll(); booted = true; return; }
    const prep = prepare(document, true);
    if (!prep) { booted = true; return; }
    // Measure lines with the real fonts, but never hold the first screen for long.
    await Promise.race([fontsReady(), wait(700)]);
    splitScope(document);
    choreograph(prep.above, prep.aboveInk);
    booted = true;
    if (!fontsDone) fontsReady().then(() => splitList.forEach((h) => { const host = h.closest('[data-reveal]'); if (h.isConnected && !(host && host.classList.contains('is-in'))) split(h); }));
  }

  function initReveal(scope = document) {
    if (!booted && scope === document) return;
    if (still()) { showAll(scope); return; }
    const prep = prepare(scope, false);
    if (!prep) return;
    fontsReady().then(() => splitScope(scope));
  }

  // Never keep content hidden behind a jump link or find-in-page.
  window.addEventListener('hashchange', () => showAll());
  document.addEventListener('shopify:section:load', (e) => { if (still()) showAll(e.target); else initReveal(e.target); enhanceButtons(e.target); });
  window.addEventListener('pagereveal', (e) => {
    if (!e.viewTransition) return;
    document.querySelectorAll('[data-reveal]:not(.is-in)').forEach((el) => { if (inView(el)) showQuick(el); });
  });

  /* =======================================================================
     Buttons — label roll
     ======================================================================= */
  function enhanceButton(btn) {
    if (btn._roll || btn.closest('[data-no-roll]') || btn.hasAttribute('data-no-roll')) return;
    if (btn.matches('[aria-disabled="true"], [disabled]')) return; // nothing to roll towards
    const nodes = [...btn.childNodes].filter((n) => !(n.nodeType === 1 && (n.tagName === 'svg' || n.tagName === 'SVG' || n.classList.contains('icon') || n.tagName === 'INPUT' || n.tagName === 'IMG')));
    const text = nodes.map((n) => n.textContent).join('').trim();
    if (!text) return;
    // leading/trailing whitespace stays outside so the icon gap is unchanged
    const first = nodes[0];
    const roll = document.createElement('span');
    roll.className = 'btn-roll';
    const a = document.createElement('span');
    const b = document.createElement('span');
    b.setAttribute('aria-hidden', 'true');
    btn.insertBefore(roll, first);
    nodes.forEach((n) => a.appendChild(n));
    // trim outer whitespace of the moved label
    if (a.firstChild && a.firstChild.nodeType === 3) a.firstChild.textContent = a.firstChild.textContent.replace(/^\s+/, '');
    if (a.lastChild && a.lastChild.nodeType === 3) a.lastChild.textContent = a.lastChild.textContent.replace(/\s+$/, '');
    roll.append(a, b);
    const sync = () => { b.innerHTML = a.innerHTML; b.querySelectorAll('[data-cart-count]').forEach((n) => n.removeAttribute('data-cart-count')); b.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id')); b.querySelectorAll('[aria-live],[role]').forEach((n) => { n.removeAttribute('aria-live'); n.removeAttribute('role'); }); };
    sync();
    new MutationObserver(sync).observe(a, { childList: true, subtree: true, characterData: true });
    btn._roll = roll;
  }
  function enhanceButtons(scope = document) {
    if (scope.nodeType !== 1 && scope !== document) return;
    if (scope.matches && scope.matches('.button')) enhanceButton(scope);
    scope.querySelectorAll('.button').forEach(enhanceButton);
  }
  enhanceButtons();
  // Late-rendered buttons (drawer re-render, section scripts)
  let pendingNodes = [];
  new MutationObserver((list) => {
    list.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1 && !n.closest('.btn-roll')) pendingNodes.push(n); }));
    if (pendingNodes.length && !pendingNodes._q) {
      pendingNodes._q = true;
      raf(() => { const ns = pendingNodes; pendingNodes = []; ns.forEach((n) => { if (n.isConnected) enhanceButtons(n); }); });
    }
  }).observe(document.body, { childList: true, subtree: true });

  /* =======================================================================
     Product card — pouch follows the pointer, shadow shifts opposite
     ======================================================================= */
  const tracking = new Set();
  let trackLoop = 0;
  function trackTick() {
    trackLoop = 0;
    tracking.forEach((st) => {
      st.x += (st.tx - st.x) * .14;
      st.y += (st.ty - st.y) * .14;
      const done = Math.abs(st.tx - st.x) < .05 && Math.abs(st.ty - st.y) < .05;
      if (done) { st.x = st.tx; st.y = st.ty; }
      st.img.style.transform = `translate3d(${st.x.toFixed(2)}px, ${st.y.toFixed(2)}px, 0)`;
      st.img.style.filter = `drop-shadow(${(-st.x * .8).toFixed(2)}px ${(26 - st.y * .6).toFixed(2)}px 22px rgba(28,23,20,.28))`;
      if (st.beans) {
        // normalised −1..1 for the beans (bean 1 drifts against the pouch, bean 2 with it; see motion.css)
        st.beans.style.setProperty('--px', (st.x / 10).toFixed(3));
        st.beans.style.setProperty('--py', ((st.y + 6) / 8).toFixed(3));
      }
      if (done && !st.active) {
        st.img.style.transform = '';
        st.img.style.filter = '';
        if (st.beans) { st.beans.style.removeProperty('--px'); st.beans.style.removeProperty('--py'); }
        st.media.classList.remove('is-tracking');
        tracking.delete(st);
      }
    });
    if (tracking.size) trackLoop = raf(trackTick);
  }
  const CARD_SEL = '.pcard, .trio__band';
  function cardState(card) {
    if (!card._track) {
      const media = card.querySelector('.pcard__media, .trio__pouch');
      const img = media && media.querySelector(':scope > img');
      if (!img || card.classList.contains('pcard--generic')) return null;
      card._track = { media, img, beans: media.querySelector('[data-beans]'), x: 0, y: 0, tx: 0, ty: 0, active: false };
    }
    return card._track;
  }
  document.addEventListener('pointerover', (e) => {
    if (!FINE.matches || still() || e.pointerType !== 'mouse') return;
    const card = e.target.closest && e.target.closest(CARD_SEL);
    if (!card || card.contains(e.relatedTarget)) return;
    const st = cardState(card);
    if (!st) return;
    st.active = true;
    st.rect = st.media.getBoundingClientRect();
    st.media.classList.add('is-tracking');
    tracking.add(st);
  });
  document.addEventListener('pointermove', (e) => {
    if (!tracking.size || e.pointerType !== 'mouse') return;
    tracking.forEach((st) => {
      if (!st.active) return;
      const r = st.rect;
      const nx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
      const ny = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
      st.tx = nx * 10;
      st.ty = ny * 8 - 6;
    });
    if (!trackLoop) trackLoop = raf(trackTick);
  }, { passive: true });
  document.addEventListener('pointerout', (e) => {
    const card = e.target.closest && e.target.closest(CARD_SEL);
    if (!card || !card._track || card.contains(e.relatedTarget)) return;
    card._track.active = false;
    card._track.tx = 0;
    card._track.ty = 0;
    if (!trackLoop) trackLoop = raf(trackTick);
  });

  /* Idle beans: run only while the card is in view and the tab is visible (CSS keys on .is-idle + html[data-beans]). */
  const beanIo = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((en) => en.target.classList.toggle('is-idle', en.isIntersecting));
  }, { rootMargin: '40px 0px' }) : null;
  function watchBeans(scope = document) {
    if (!beanIo || still()) return;
    scope.querySelectorAll('[data-beans]').forEach((b) => {
      const host = b.closest(CARD_SEL);
      if (host && !host._beans) { host._beans = true; beanIo.observe(host); }
    });
  }
  const syncBeans = () => { root.dataset.beans = document.hidden ? 'paused' : 'on'; };
  document.addEventListener('visibilitychange', syncBeans);
  syncBeans();
  watchBeans();
  document.addEventListener('shopify:section:load', (e) => watchBeans(e.target));
  document.addEventListener('cart:updated', () => watchBeans());

  /* =======================================================================
     Header — hide on scroll down, show on scroll up
     ======================================================================= */
  const header = document.querySelector('[data-site-header]');
  let lastY = window.scrollY;
  let scrollQueued = false;
  let pinUntil = 0;
  function headerBlocked() {
    if (!header) return true;
    const sheet = document.querySelector('[data-menu-sheet]');
    const drawer = document.querySelector('[data-cart-drawer]');
    return (sheet && sheet.classList.contains('is-open')) || (drawer && drawer.classList.contains('is-open'))
      || header.matches(':focus-within') || performance.now() < pinUntil || designMode();
  }
  function setHeaderHidden(hidden) { if (header) header.classList.toggle('is-hidden', hidden && !headerBlocked()); }
  function onHeaderScroll() {
    scrollQueued = false;
    const y = window.scrollY;
    const dy = y - lastY;
    if (header) header.classList.toggle('is-scrolled', y > 4);
    tracking.forEach((st) => { if (st.active) st.rect = st.media.getBoundingClientRect(); });
    if (y < 200) setHeaderHidden(false);
    else if (dy > 6) setHeaderHidden(true);
    else if (dy < -6) setHeaderHidden(false);
    if (Math.abs(dy) > 6 || y < 200) lastY = y;
  }
  // the one scroll listener: header state + tracking-card rects, always inside a frame
  window.addEventListener('scroll', () => { if (!scrollQueued) { scrollQueued = true; raf(onHeaderScroll); } }, { passive: true });
  if (header) {
    onHeaderScroll();
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
  }
  function pinHeader(ms) { pinUntil = performance.now() + ms; if (header) header.classList.remove('is-hidden'); }

  /* =======================================================================
     Lamplight — one pool of light slides under the hovered / focused route
     (clip-path only; no rAF: it moves on pointer and focus events)
     ======================================================================= */
  (function lamplight() {
    const nav = document.querySelector('.site-nav');
    if (!nav || still() || !('clipPath' in document.documentElement.style || 'webkitClipPath' in document.documentElement.style)) return;
    const pool = document.createElement('span');
    pool.className = 'site-nav__pool';
    pool.setAttribute('aria-hidden', 'true');
    nav.prepend(pool);
    nav.classList.add('has-pool');
    const links = () => [...nav.querySelectorAll('a')];
    const current = () => nav.querySelector('a[aria-current="page"]');
    const place = (a) => {
      const nr = nav.getBoundingClientRect();
      const r = a.getBoundingClientRect();
      const l = Math.max(0, r.left - nr.left);
      const right = Math.max(0, nr.right - r.right);
      const clip = `inset(0 ${right}px 0 ${l}px round 999px)`;
      if (!pool.classList.contains('is-on')) {
        // from the dark: appear in place (fade only), no slide in from the edge
        pool.style.transition = 'none';
        pool.style.clipPath = clip;
        void pool.offsetWidth;
        pool.style.transition = '';
      } else pool.style.clipPath = clip;
      pool.classList.add('is-on');
    };
    const rest = () => { const c = current(); if (c) place(c); else pool.classList.remove('is-on'); };
    // start at the active route (no transition on first paint)
    pool.style.transition = 'none';
    rest();
    void pool.offsetWidth;
    pool.style.transition = '';
    links().forEach((a) => {
      a.addEventListener('pointerenter', () => place(a));
      a.addEventListener('focusin', () => place(a));
    });
    nav.addEventListener('pointerleave', rest);
    nav.addEventListener('focusout', (e) => { if (!nav.contains(e.relatedTarget)) rest(); });
    window.addEventListener('resize', () => { pool.style.transition = 'none'; rest(); void pool.offsetWidth; pool.style.transition = ''; }, { passive: true });
  })();

  /* =======================================================================
     Mobile menu — circle grows from the button
     ======================================================================= */
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-menu-toggle]');
    const sheet = document.querySelector('[data-menu-sheet]');
    if (!t || !sheet) return;
    const r = t.getBoundingClientRect();
    sheet.style.setProperty('--menu-x', `${Math.round(r.left + r.width / 2)}px`);
    sheet.style.setProperty('--menu-y', `${Math.round(r.top + r.height / 2)}px`);
  }, true);

  /* =======================================================================
     Cart drawer — staggered lines, ship meter, removal
     ======================================================================= */
  let lastPct = null;
  let lastKeys = [];
  const drawerEl = () => document.querySelector('[data-cart-drawer]');
  const lineKey = (li) => { const b = li.querySelector('[data-key]'); return b ? b.dataset.key : ''; };
  function meterPct() { const s = document.querySelector('[data-cart-drawer] .ship-meter__bar span'); return s ? parseFloat(s.style.width) || 0 : null; }
  function animateMeter(fromPct, delay) {
    const s = document.querySelector('[data-cart-drawer] .ship-meter__bar span');
    if (!s || still()) return;
    const to = parseFloat(s.style.width) || 0;
    if (!to) return;
    const from = Math.max(0, Math.min(1, (fromPct || 0) / to));
    if (from === 1) return;
    s.animate([{ transform: `scaleX(${from})` }, { transform: 'none' }], { duration: T.enter, delay, easing: EASE, fill: 'backwards' });
  }
  function onDrawerOpen(d) {
    if (still()) return;
    let i = 0;
    d.querySelectorAll('.line, .upsell-card, .empty-state > *').forEach((el) => el.style.setProperty('--i', i++));
    d.classList.remove('is-entering');
    void d.offsetWidth;
    d.classList.add('is-entering');
    clearTimeout(d._enterT);
    d._enterT = setTimeout(() => d.classList.remove('is-entering'), 140 + i * T.stagger + T.move + 60);
    animateMeter(0, 260);
    lastPct = meterPct();
  }
  const dObs = new MutationObserver(() => {
    const d = drawerEl();
    if (!d) return;
    const open = d.classList.contains('is-open');
    if (open && !d._wasOpen) onDrawerOpen(d);
    d._wasOpen = open;
  });
  if (drawerEl()) dObs.observe(drawerEl(), { attributes: true, attributeFilter: ['class'] });
  document.addEventListener('cart:updated', () => {
    const d = drawerEl();
    if (!d) return;
    const keys = [...d.querySelectorAll('.line')].map(lineKey);
    if (d.classList.contains('is-open') && !still()) {
      animateMeter(lastPct, 0);
      d.querySelectorAll('.line').forEach((li) => { if (!lastKeys.includes(lineKey(li))) li.classList.add('is-new'); });
    }
    lastKeys = keys;
    lastPct = meterPct();
  });
  if (drawerEl()) { lastKeys = [...drawerEl().querySelectorAll('.line')].map(lineKey); lastPct = meterPct(); }
  // removing a line: fold it away while the cart updates
  document.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('[data-cart-drawer] [data-line-change][data-quantity="0"]');
    if (!b || still()) return;
    const li = b.closest('.line');
    if (!li || li._leaving) return;
    li._leaving = true;
    const h = li.offsetHeight;
    li.style.overflow = 'hidden';
    li.animate([
      { opacity: 1, transform: 'none', height: `${h}px`, paddingTop: getComputedStyle(li).paddingTop, paddingBottom: getComputedStyle(li).paddingBottom },
      { opacity: 0, transform: 'translateX(16px)', height: `${h}px`, offset: .45 },
      { opacity: 0, transform: 'translateX(16px)', height: '0px', paddingTop: '0px', paddingBottom: '0px' },
    ], { duration: T.roll, easing: EASE, fill: 'forwards' });
  }, true);

  /* =======================================================================
     Cart count roll + pill ring
     ======================================================================= */
  let landingAt = 0;
  function rollCount(el, from, to) {
    const run = () => {
      if (!el.isConnected) return;
      const r = el.querySelector(':scope > .count-roll');
      if (r) r.classList.add('is-rolling');
      setTimeout(() => { if (el.querySelector(':scope > .count-roll')) el.textContent = to; }, T.ui + 40);
    };
    if (still() || from === to) { el.textContent = to; return; }
    el.textContent = '';
    const roll = document.createElement('span');
    roll.className = 'count-roll';
    const a = document.createElement('span'); a.textContent = from; a.setAttribute('aria-hidden', 'true');
    const b = document.createElement('span'); b.textContent = to;
    roll.append(a, b);
    el.appendChild(roll);
    const wait = landingAt - performance.now();
    if (wait > 0) setTimeout(() => raf(run), wait); else raf(() => raf(run));
  }
  document.querySelectorAll('[data-cart-count]').forEach((el) => { el._val = el.textContent.trim(); });
  const countObs = new MutationObserver((list) => {
    const seen = new Set();
    list.forEach((m) => {
      const el = m.target.nodeType === 1 ? m.target.closest('[data-cart-count]') : m.target.parentElement && m.target.parentElement.closest('[data-cart-count]');
      if (!el || seen.has(el)) return;
      seen.add(el);
      if (el.querySelector(':scope > .count-roll')) return; // our own markup
      const val = el.textContent.trim();
      if (el._val === undefined) { el._val = val; return; }
      if (val === el._val) return;
      const from = el._val;
      el._val = val;
      rollCount(el, from, val);
    });
  });
  const header2 = document.querySelector('.section-header') || document.body;
  countObs.observe(header2, { childList: true, subtree: true, characterData: true });
  function ring() {
    document.querySelectorAll('.site-header .cart-pill').forEach((p) => {
      p.classList.remove('is-ringing');
      void p.offsetWidth;
      p.classList.add('is-ringing');
      setTimeout(() => p.classList.remove('is-ringing'), 800);
    });
  }

  /* =======================================================================
     Add-to-cart flight
     ======================================================================= */
  const pouchSrc = (h) => (window.Boonsoir && window.Boonsoir.pouches && window.Boonsoir.pouches[h]) || null;
  const visibleImg = (img) => img && img.complete !== false && inView(img) && img.getBoundingClientRect().width > 8;
  function handleOf(path) { const m = /\/products\/([^/?#]+)/.exec(path || ''); return m ? decodeURIComponent(m[1]) : null; }

  function flightSources(form, btn) {
    const anchor = (btn || form).getBoundingClientRect();
    const fromButton = (src, photo) => ({ src, photo, rect: { left: anchor.left + anchor.width / 2 - 36, top: anchor.top + anchor.height / 2 - 54, width: 72, height: 108 } });
    const isTrio = form.classList.contains('trio-form') || form.querySelectorAll('input[name^="items["][name$="[id]"]').length >= 3;
    if (isTrio) {
      const scope = form.closest('.shopify-section') || document;
      return ['smooth', 'bright', 'rich'].map((h) => {
        const img = [...scope.querySelectorAll(`img[src*="pouch-${h}"]`)].find(visibleImg);
        if (img) return { src: img.currentSrc || img.src, rect: img.getBoundingClientRect() };
        const src = pouchSrc(h);
        return src ? fromButton(src) : null;
      }).filter(Boolean);
    }
    // product card / upsell / compare / any block with a pouch
    const block = form.closest('.pcard, .upsell-card, [data-vt-card], li, tr, article');
    if (block) {
      const img = [...block.querySelectorAll('img')].find(visibleImg);
      if (img) return [{ src: img.currentSrc || img.src, rect: img.getBoundingClientRect(), photo: !/pouch-/.test(img.src) }];
    }
    // product page
    const pdp = form.closest('[data-pdp]');
    if (pdp) {
      const h = pdp.getAttribute('data-coffee');
      const lead = pdp.querySelector('[data-vt-target="media"] img, .pdp-gallery__tile--lead img');
      if (h && pouchSrc(h)) {
        if (lead && inView(lead)) {
          const r = lead.getBoundingClientRect();
          const w = r.width * .5, hh = w * 1.5;
          return [{ src: pouchSrc(h), rect: { left: r.left + (r.width - w) / 2, top: r.top + (r.height - hh) / 2, width: w, height: hh } }];
        }
        return [fromButton(pouchSrc(h))];
      }
      if (lead && inView(lead)) return [{ src: lead.currentSrc || lead.src, rect: lead.getBoundingClientRect(), photo: true }];
    }
    const sec = form.closest('section, .shopify-section');
    const near = sec && [...sec.querySelectorAll('img[src*="pouch-"]')].find(visibleImg);
    if (near) return [{ src: near.currentSrc || near.src, rect: near.getBoundingClientRect() }];
    return [];
  }

  function headerOffsetY() {
    if (!header) return 0;
    const t = getComputedStyle(header).transform;
    if (!t || t === 'none') return 0;
    try { return -new DOMMatrixReadOnly(t).m42; } catch (e) { return 0; }
  }

  function flyOne(s, target, delay) {
    const el = document.createElement('div');
    el.className = 'bs-flight' + (s.photo ? ' bs-flight--photo' : '');
    el.setAttribute('aria-hidden', 'true');
    const r = s.rect;
    Object.assign(el.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
    const y = document.createElement('span');
    y.className = 'bs-flight__y';
    const img = document.createElement('img');
    img.src = s.src;
    img.alt = '';
    y.appendChild(img);
    el.appendChild(y);
    document.body.appendChild(el);
    const dx = (target.left + target.width / 2) - (r.left + r.width / 2);
    const dy = (target.top + target.height / 2) - (r.top + r.height / 2);
    const scale = Math.min(.35, (target.height * 1.2) / r.height);
    const dur = 760;
    const opts = { duration: dur, delay, fill: 'both' };
    // X and Y on separate layers with different curves: a true arc, no corner, no overshoot.
    el.animate([{ transform: 'translate3d(0,0,0)' }, { transform: `translate3d(${dx}px,0,0)` }], { ...opts, easing: 'cubic-bezier(.3, 0, .2, 1)' });
    const a = y.animate([
      { transform: 'translate3d(0,0,0) scale(1)', opacity: 1 },
      { transform: `translate3d(0,${dy}px,0) scale(${scale})`, opacity: 1, offset: .86 },
      { transform: `translate3d(0,${dy}px,0) scale(${scale})`, opacity: 0 },
    ], { ...opts, easing: EASE });
    a.finished.catch(() => {}).then(() => el.remove());
    setTimeout(() => el.remove(), dur + delay + 400);
    return dur + delay;
  }

  // Measure where the pouch starts before the cart re-render can move or replace it.
  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('form[data-ajax-cart]');
    if (!form || still()) return;
    try { form._flight = { sources: flightSources(form, e.submitter || form.querySelector('[type="submit"]')), at: performance.now() }; } catch (err) { form._flight = null; }
    // bring the cart pill back now, so it stands still before the pouch takes off
    const d = drawerEl();
    if (!(d && d.classList.contains('is-open'))) pinHeader(2500);
  }, true);

  /** Called by theme.js after a successful add. Returns ms to wait before opening the drawer. */
  function flight(form, btn) {
    if (still() || !form) return 0;
    const d = drawerEl();
    const drawerOpen = d && d.classList.contains('is-open');
    let target = null;
    let toPill = false;
    if (drawerOpen) {
      const line = d.querySelector('.line__media');
      if (line) target = line.getBoundingClientRect();
    } else {
      const pill = document.querySelector('.site-header .cart-pill');
      if (pill) {
        pinHeader(1600);
        const r = pill.getBoundingClientRect();
        const off = headerOffsetY();
        target = { left: r.left, top: r.top + off, width: r.width, height: r.height };
        toPill = true;
      }
    }
    if (!target) return 0;
    const sources = form._flight && performance.now() - form._flight.at < 15000 ? form._flight.sources : flightSources(form, btn);
    form._flight = null;
    if (!sources.length) { if (toPill) { landingAt = performance.now(); ring(); } return 0; }
    let end = 0;
    sources.forEach((s, i) => { end = Math.max(end, flyOne(s, target, i * 90)); });
    if (!toPill) return 0;
    landingAt = performance.now() + end - 120;
    setTimeout(ring, end - 120);
    // let the count roll and the ring register before the drawer slides over the pill
    return end + 260;
  }

  /* =======================================================================
     Page transitions — the pouch travels (pageswap side; pagereveal is inline in <head>)
     ======================================================================= */
  let lastLink = null;
  let lastLinkAt = 0;
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (a) { lastLink = a; lastLinkAt = performance.now(); }
  }, true);
  let swapped = [];
  const clearSwapped = () => { swapped.forEach((el) => { el.style.viewTransitionName = ''; }); swapped = []; };
  // Only the pouch morph (card ⇄ product page) gets a transition; every other navigation swaps instantly.
  function pickSwap(e) {
    let url;
    try { url = new URL(e.activation && e.activation.entry && e.activation.entry.url); } catch (err) { return; }
    if (url.origin !== location.origin) return;
    const toH = handleOf(url.pathname);
    const here = handleOf(location.pathname);
    const name = (el, n) => { if (el) { el.style.viewTransitionName = n; swapped.push(el); } };
    const store = (o) => { try { sessionStorage.setItem('bs-vt', JSON.stringify({ ...o, t: Date.now() })); } catch (err) { /* private mode */ } };
    if (toH) {
      const a = lastLink && performance.now() - lastLinkAt < 5000 && handleOf(new URL(lastLink.href, location.href).pathname) === toH ? lastLink : null;
      if (!a) return;
      const card = a.closest('[data-vt-card]');
      let media = card ? card.querySelector('[data-vt-media]') : null;
      const title = card ? card.querySelector('[data-vt-title]') : null;
      if (!media) media = a.matches('.menu-sheet__coffee') ? a : a.querySelector('img');
      if (!media || !inView(media)) return;
      name(media, 'vt-media');
      if (title && inView(title)) name(title, 'vt-title');
      store({ dir: 'to', handle: toH });
    } else if (here) {
      const media = document.querySelector('[data-vt-target="media"], .pdp-gallery__tile--lead');
      const title = document.querySelector('[data-vt-target="title"]');
      if (media && inView(media)) name(media, 'vt-media');
      if (title && inView(title)) name(title, 'vt-title');
      if (swapped.length) store({ dir: 'back', handle: here });
    }
  }
  window.addEventListener('pageswap', (e) => {
    const vt = e.viewTransition;
    if (!vt) return;
    [vt.ready, vt.finished, vt.updateCallbackDone].forEach((p) => p && p.catch(() => {}));
    if (RM.matches) { vt.skipTransition(); return; }
    clearSwapped();
    root.removeAttribute('data-vt-morph');
    try { pickSwap(e); } catch (err) { clearSwapped(); }
    if (!swapped.length) vt.skipTransition();
  });
  window.addEventListener('pageshow', (e) => { if (e.persisted) clearSwapped(); });

  /* =======================================================================
     Footer wordmark — fallback when scroll-driven animations are missing
     ======================================================================= */
  const big = document.querySelector('.site-footer__big');
  if (big && !(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()'))) {
    if (still()) big.classList.add('is-in'); else watch(big, () => big.classList.add('is-in'));
  }

  boot();

  window.BoonsoirMotion = { initReveal, showAll, flight, enhanceButtons, split, watch, pinHeader, tokens: T };
})();
